// /DeviceConfigs/DeviceConfigFormModule/TabCloakroom.js
import React, { useMemo, useState, useCallback } from 'react';
import { Row, Col, Form, InputNumber, Alert, Button, Space, Popconfirm, message } from 'antd';
import axios from '../../../config/axiosInstance'; // adjust path if needed

const TabCloakroom = ({ form, deviceConfigId }) => {
    const [forceEditStart, setForceEditStart] = useState(false);
    const [loadingReset, setLoadingReset] = useState(false);

    const start = Form.useWatch('CloakroomTagStart', form);
    const current = Form.useWatch('CloakroomTagCurrent', form);

    const shouldAutoLockStart = useMemo(() => {
        if (start == null || current == null) return false;
        const s = Number(start);
        const c = Number(current);
        if (Number.isNaN(s) || Number.isNaN(c)) return false;
        return c >= s && s >= 1;
    }, [start, current]);

    const startDisabled = shouldAutoLockStart && !forceEditStart;

    const resetSequence = useCallback(async () => {
        if (!form) return; // evita el "getFieldValue undefined"
        if (!deviceConfigId) {
            message.error('Missing DeviceConfig ID (cannot reset sequence).');
            return;
        }

        try {
            setLoadingReset(true);
            const res = await axios.post(`/deviceConfigs/cloakroom/${deviceConfigId}/reset-sequence`);

            // Backend devuelve { ok:true, CloakroomTagStart:0, CloakroomTagCurrent:0 }
            if (res?.data?.ok) {
                form.setFieldsValue({
                    CloakroomTagStart: res.data.CloakroomTagStart,
                    CloakroomTagCurrent: res.data.CloakroomTagCurrent,
                });
                setForceEditStart(true);
                message.success('Cloakroom sequence reset.');
            } else {
                message.error('Reset failed.');
            }
        } catch (e) {
            console.error(e);
            message.error('Reset failed.');
        } finally {
            setLoadingReset(false);
        }
    }, [form, deviceConfigId]);

    const lockStartAgain = () => setForceEditStart(false);

    return (
        <>
            <Alert
                type={startDisabled ? 'warning' : 'info'}
                showIcon
                message="Cloakroom tag sequence"
                description={
                    startDisabled
                        ? `Start is locked because current (${current}) is >= start (${start}). Reset to unlock Start for editing.`
                        : 'Set the starting number. Current is backend-managed and saved with the config.'
                }
                style={{ marginBottom: 16 }}
            />

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="CloakroomTagStart"
                        label="Tag start number"
                        rules={[
                            { required: true, message: 'Please set the starting tag number' },
                            { type: 'number', min: 1, message: 'Must be >= 1' },
                        ]}
                    >
                        <InputNumber min={1} step={1} style={{ width: '100%' }} disabled={startDisabled} />
                    </Form.Item>

                    <Space>
                        <Popconfirm
                            title="Reset sequence?"
                            description="This will set Start=0 and Current=0 (backend) and unlock Start so you can change it."
                            okText="Reset"
                            cancelText="Cancel"
                            onConfirm={resetSequence}
                            disabled={!deviceConfigId}
                        >
                            <Button type="primary" loading={loadingReset}>
                                Reset sequence
                            </Button>
                        </Popconfirm>

                        <Button onClick={lockStartAgain} disabled={!forceEditStart}>
                            Lock start
                        </Button>
                    </Space>
                </Col>

                <Col xs={24} md={12}>
                    <Form.Item
                        name="CloakroomTagCurrent"
                        label="Current counter"
                        tooltip="Backend-managed counter."
                    >
                        <InputNumber style={{ width: '100%' }} disabled />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );
};

export default TabCloakroom;
